import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { isIOS } from 'react-device-detect';

import type { TAuthorNameContentItem } from '@src/types/wysiwyg_editor';
import Rectangle from '../../wysiwyg_editor/page_content_items/Rectangle';
import TextEditor from '@src/components/wysiwyg_editor/TextEditor';
import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import RenderedText from '@src/components/wysiwyg_editor/page_content_items/text/RenderedText';
import { setAuthorName } from '@src/actions/stencils/editor';
import useDomLocationInWysiwygContainer from '../../../hooks/wysiwyg_editor/useDomLocationInWysiwygContainer';
import { Portal } from '../../Portal';
import TextOverflowIndicator from '@src/components/stencils/TextOverflowIndicator';
import { stylesToDefaultMarkValues } from '@src/utils/wysiwyg_editor/styles';
import {
  calculateOverflowContainerLocation,
  calculateTextOverflows
} from '@src/utils/wysiwyg_editor/overflow';
import { emptyDocument } from '@src/utils/proseMirror';
import useOnClickOutside from 'use-onclickoutside';
import ErrorBoundary from '@src/components/ErrorBoundary';
import { defaultMarkValues } from '@src/prose_mirror/schemas/TextSchema';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';

const defaultStyles = {
  color: '#000000'
};

const overflowIndicatorLocation = {
  top: 0,
  right: -35
};

function DefaultText(props: {
  location: any,
  viewbox: any,
  styles: any,
  scaleBasis: { height: number, width: number }
}) {
  return (
    <tspan
      x={props.location.x * props.viewbox.width}
      y={props.location.y * props.viewbox.height}
      fontFamily={props.styles.face}
      fontSize={
        (props.viewbox.height / props.scaleBasis.height) * props.styles.size
      }
      fontStyle={props.styles.style}
      fontWeight={props.styles.weight}
      fill={props.styles.color || defaultStyles.color}
    >
      (Add your name)
    </tspan>
  );
}

function AuthorName({ data }: { data: TAuthorNameContentItem }) {
  const dispatch = useDispatch();
  const domElementsRef = useRef(null);
  const [textOverflows, setTextOverflows] = useState(false);
  const { domScaleBasisDimensions, productionRender, viewbox,  } = useContext(WysiwygEditorStateContext);
  const [editorShown, setEditorShown] = useState(false);
  const [textDocument, setTextDocument] = useState(() =>
    data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(
          'left',
          stylesToDefaultMarkValues(
            { ...defaultMarkValues, ...defaultStyles },
            data.styles
          )
        )
  );
  const [textContent, setTextContent] = useState(
    () => textDocument.textContent
  );
  // Windows devices don't render the thinner line in the SVG, so we display a
  // thicker line in the builder than in the final production render.
  const [horizontalRuleHeight] = useState(
    productionRender ? 0.0001941 : 0.000776398
  );
  const containerLocationInDom = useDomLocationInWysiwygContainer(
    data.containerLocation
  );
  const contentLocationInDom = useDomLocationInWysiwygContainer(
    data.contentLocation
  );

  useEffect(() => {
    const newDocument = data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(
          'left',
          stylesToDefaultMarkValues(
            { ...defaultMarkValues, ...defaultStyles },
            data.styles
          )
        );

    const newTextContent = newDocument.textContent;

    setTextDocument(newDocument);
    setTextContent(newTextContent);
    dispatch(setAuthorName(newTextContent));
  }, [data.content, data.styles, dispatch]);

  useEffect(() => {
    setTextOverflows(
      calculateTextOverflows(
        textDocument,
        data.contentLocation.height,
        data.contentLocation.width
      )
    );
  }, [
    data.contentLocation.height,
    data.contentLocation.width,
    setTextOverflows,
    textDocument
  ]);

  useOnClickOutside(domElementsRef, () => setEditorShown(false));

  // If we don't hard code the height of "From" to be the same as the rest of
  // the text, it is very slightly out of line with it.
  const firstText = textDocument.toWordsWithAttributeAndPositions[0];
  const labelY =
    (firstText
      ? firstText.pos.y
      : data.placeholderLocation
      ? data.placeholderLocation.y
      : 0) * viewbox.height;

  return (
    <Fragment>
      <g style={{ cursor: editorShown ? 'default' : 'pointer' }}>
        {!productionRender || textContent
          ? [
              <g
                key="from-label"
                transform={`translate(${
                  (data.placeholderLocation ? data.placeholderLocation.x : 0) *
                  viewbox.width
                } ${data.contentLocation.y * viewbox.height})`}
              >
                <text>
                  <tspan
                    x={0}
                    y={labelY}
                    fontFamily={data.styles.face}
                    fontSize={
                      (viewbox.height / domScaleBasisDimensions.height) *
                      data.styles.size
                    }
                    fontStyle={data.styles.style}
                    fontWeight={data.styles.weight}
                    fill={data.styles.color || defaultStyles.color}
                  >
                    From
                  </tspan>
                </text>
              </g>,
              <Rectangle
                location={{ x: 0, y: 0.055888803 }}
                height={horizontalRuleHeight}
                width={0.720588235}
                backgroundColor="#757575"
                key="horizontal-rule"
              />
            ]
          : null}

        {editorShown ? null : !productionRender || textContent ? (
          <RenderedText
            editorContent={textDocument}
            maxLines={1}
            textContent={textContent}
            location={data.contentLocation}
            viewbox={viewbox}
          >
            <DefaultText
              location={data.placeholderLocation}
              viewbox={viewbox}
              styles={data.styles}
              scaleBasis={domScaleBasisDimensions}
            />
          </RenderedText>
        ) : null}
      </g>

      <Portal htmlId="wysiwyg-editor-portal">
        <div ref={domElementsRef}>
          {!productionRender && containerLocationInDom ? (
            <div
              className={classnames('wysiwyg-editor-canvas-element-editindicatorbox', {
                'wysiwyg-editor-canvas-element-editindicatorbox--active': editorShown
              })}
              style={{
                background: 'transparent',
                cursor: 'pointer',
                height: containerLocationInDom.height * 2.1,
                left: containerLocationInDom.x - 10,
                position: 'absolute',
                top: containerLocationInDom.y,
                width: containerLocationInDom.width + 20
              }}
              onPointerDown={
                // I have no idea why non-ios is requiring a timeout
                isIOS
                  ? () => setEditorShown(true)
                  : () => setTimeout(() => setEditorShown(true), 0)
              }
            />
          ) : null}

          {productionRender ? null : textOverflows &&
            !editorShown &&
            containerLocationInDom &&
            contentLocationInDom ? (
            <div
              style={{
                position: 'absolute',
                ...calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  containerLocationInDom,
                  contentLocationInDom
                )
              }}
            >
              <div style={{ position: 'relative' }}>
                <TextOverflowIndicator position={{ top: 0, left: 0 }} show />
              </div>
            </div>
          ) : null}

          {contentLocationInDom && containerLocationInDom && editorShown ? (
            <ErrorBoundary
              resetOnChanges={textDocument}
              fallbackComponent={
                <p
                  style={{
                    background: '#ff0000',
                    color: '#ffffff',
                    fontWeight: 'normal',
                    position: 'absolute',
                    top:
                      containerLocationInDom.y + (contentLocationInDom?.y || 0),
                    left:
                      containerLocationInDom.x + (contentLocationInDom?.x || 0),
                    minHeight:
                      contentLocationInDom?.height ??
                      containerLocationInDom.height,
                    width:
                      contentLocationInDom?.width ??
                      containerLocationInDom.width,
                    cursor: 'default'
                  }}
                >
                  There was a problem displaying the text editor. You should{' '}
                  <a
                    onClick={() => {
                      setTextDocument(
                        emptyDocument(
                          'left',
                          stylesToDefaultMarkValues(
                            { ...defaultMarkValues, ...defaultStyles },
                            data.styles
                          )
                        )
                      );
                    }}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#ffffff',
                      fontWeight: 'bold'
                    }}
                  >
                    click here
                  </a>{' '}
                  to reset the content in this text box.
                </p>
              }
            >
              <TextEditor
                defaultAlignment="left"
                initialContent={textDocument}
                location={{
                  height: contentLocationInDom.height,
                  width: contentLocationInDom.width,
                  x: containerLocationInDom.x + contentLocationInDom.x,
                  y: containerLocationInDom.y + contentLocationInDom.y
                }}
                textItem={data}
                localDefaultMarkValues={stylesToDefaultMarkValues(
                  { ...defaultMarkValues, ...defaultStyles },
                  data.styles
                )}
                overflowIndicatorPosition={overflowIndicatorLocation}
                textOverflows={textOverflows}
                isSingleLine
                focusOnRender
              />
            </ErrorBoundary>
          ) : null}
        </div>
      </Portal>
    </Fragment>
  );
}

export default AuthorName;
